import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { Paginated } from '@lms/shared';
import { api, toApiError } from './api';

/**
 * Query keys are declared in one place so an invalidation after a mutation
 * can never drift from the key a list actually used.
 */
export const qk = {
  users: (params?: unknown) => ['users', params] as const,
  user: (id: string) => ['user', id] as const,
  roles: () => ['roles'] as const,
  permissions: () => ['permissions'] as const,
  departments: (includeInactive?: boolean) => ['departments', includeInactive] as const,
  designations: (includeInactive?: boolean) => ['designations', includeInactive] as const,
  bulkTypes: () => ['bulk-types'] as const,
  bulkJob: (id: string) => ['bulk-job', id] as const,
};

// ── Types the UI consumes ──────────────────────────────────────────────────

export interface UserRow {
  id: string;
  employeeCode: string | null;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: 'ACTIVE' | 'DISABLED' | 'INVITED';
  lastLoginAt: string | null;
  mustChangePassword: boolean;
  department: { id: string; name: string } | null;
  designation: { id: string; name: string } | null;
  roles: { id: string; name: string; slug: string }[];
  managedDepartments: { id: string; name: string }[];
}

export interface RoleRow {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isSystem: boolean;
  userCount: number;
  permissionKeys: string[];
}

export interface PermissionModule {
  module: string;
  permissions: { key: string; description: string }[];
}

export interface DepartmentRow {
  id: string;
  name: string;
  code: string;
  description: string | null;
  isActive: boolean;
  memberCount: number;
  managerCount: number;
}

export interface DesignationRow {
  id: string;
  name: string;
  code: string;
  description: string | null;
  isActive: boolean;
  userCount: number;
}

// ── Queries ────────────────────────────────────────────────────────────────

export function useUsers(params: Record<string, unknown>) {
  return useQuery({
    queryKey: qk.users(params),
    queryFn: async () => {
      const { data } = await api.get<Paginated<UserRow>>('/users', { params });
      return data;
    },
    // Keeps the previous page visible while the next one loads, so paging
    // through 1700 users doesn't flash an empty table each time.
    placeholderData: (previous) => previous,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: qk.roles(),
    queryFn: async () => {
      const { data } = await api.get<{ items: RoleRow[] }>('/org/roles');
      return data.items;
    },
  });
}

export function usePermissionCatalogue() {
  return useQuery({
    queryKey: qk.permissions(),
    queryFn: async () => {
      const { data } = await api.get<{ modules: PermissionModule[] }>('/org/permissions');
      return data.modules;
    },
    // The catalogue only changes when the code changes.
    staleTime: Infinity,
  });
}

export function useDepartments(includeInactive = false) {
  return useQuery({
    queryKey: qk.departments(includeInactive),
    queryFn: async () => {
      const { data } = await api.get<{ items: DepartmentRow[] }>('/org/departments', {
        params: { includeInactive },
      });
      return data.items;
    },
  });
}

export function useDesignations(includeInactive = false) {
  return useQuery({
    queryKey: qk.designations(includeInactive),
    queryFn: async () => {
      const { data } = await api.get<{ items: DesignationRow[] }>('/org/designations', {
        params: { includeInactive },
      });
      return data.items;
    },
  });
}

// ── Mutation helper ────────────────────────────────────────────────────────

/**
 * Wraps a mutation with consistent toasts and cache invalidation, so every
 * screen reports failures the same way instead of each inventing its own.
 */
export function useApiMutation<TVariables, TData>(options: {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage?: string | ((data: TData) => string);
  /** Readonly-friendly, so the `as const` query keys in `qk` can be passed directly. */
  invalidate?: readonly (readonly unknown[])[];
  onSuccess?: (data: TData) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: options.mutationFn,
    onSuccess: (data) => {
      if (options.successMessage) {
        toast.success(
          typeof options.successMessage === 'function'
            ? options.successMessage(data)
            : options.successMessage,
        );
      }
      for (const key of options.invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: [...key] });
      }
      options.onSuccess?.(data);
    },
    onError: (error) => {
      const apiError = toApiError(error);
      // Field-level problems are shown inline by the form; the toast carries
      // the headline so a failure is never silent.
      toast.error(apiError.message);
    },
  });
}
